import { useJobillaOrgId } from '@/hooks'
import { useWhatsAppTemplates } from '@/hooks/use-whatsapp-templates'
import { logger } from '@/ui'
import type { ConfirmationDialogRef } from '@/ui/rapid-ui'
import {
	useWhatsAppEmbeddedSignupActivate,
	useWhatsAppEmbeddedSignupDisconnectWhatsApp,
	useWhatsAppEmbeddedSignupResetSignup,
	useWhatsAppEmbeddedSignupRetryProvisioning,
	useWhatsAppEmbeddedSignupStatus,
	whatsAppTemplateIndexQueryKey,
} from '@jobilla/nexus-sdk'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { TemplateFormData } from '../whatsapp-template-creator'
import { apiErrorDisplay, apiErrorMessage, isUserActionableError, type TranslationKey } from './api-error'
import { deriveInactivityState, formatInactivityDate } from './inactivity-state'

export type ProvisioningStatus =
	| 'purchasing_number'
	| 'registering_on_meta'
	| 'awaiting_verification'
	| 'verification_submitted'
	| 'submitting_verification'
	| 'creating_templates'
	| 'syncing_templates'
	| 'completed'
	| 'failed'
	| null

export function useWhatsAppSettingsViewModel() {
	const { t, i18n } = useTranslation('common')
	const organisationId = useJobillaOrgId()
	const queryClient = useQueryClient()

	const [error, setError] = useState('')
	const [success, setSuccess] = useState('')
	const [isCreatingTemplate, setIsCreatingTemplate] = useState(false)
	const [showArchived, setShowArchived] = useState(false)
	const confirmDialogRef = useRef<ConfirmationDialogRef>(null)
	const hasTrackedSettingsViewRef = useRef(false)

	// --- Queries ---

	const signupStatusQuery = useWhatsAppEmbeddedSignupStatus(organisationId, {
		queryKey: ['whatsapp-signup-status', organisationId],
		enabled: Boolean(organisationId),
		retry: false,
	})

	const signupStatus = signupStatusQuery.data
	const hasOwnWaba = Boolean(signupStatus?.hasCredentials)
	const isActive = Boolean(signupStatus?.isActive)
	const onboardingStatus = signupStatus?.onboardingStatus as string | null
	const wabaDisplayName = signupStatus?.wabaDisplayName ?? null
	const provisioningStatus = (signupStatus?.provisioningStatus ?? null) as ProvisioningStatus
	const provisioningError = signupStatus?.provisioningError ?? null
	const verificationRequestFailed = Boolean(signupStatus?.verificationRequestFailed)
	const resendCooldownRemaining = Number(signupStatus?.resendCooldownRemaining) || 0
	const verificationAutoAttempts = Number(signupStatus?.verificationAutoAttempts) || 0
	const verificationOnHold = Boolean(signupStatus?.verificationOnHold)
	const firstContactAutomationEnabled = Boolean(signupStatus?.firstContactAutomationEnabled)
	const firstContactTemplateId = signupStatus?.firstContactTemplateId ?? null
	const firstContactScoreThreshold = signupStatus?.firstContactScoreThreshold ?? null
	const whatsappEnabled = Boolean(signupStatus?.whatsappEnabled)
	const { showInactivityWarning, wasDeactivatedForInactivity } = deriveInactivityState({
		hasCredentials: hasOwnWaba,
		deactivationReason: signupStatus?.deactivationReason ?? null,
		scheduledDeactivationAt: signupStatus?.scheduledDeactivationAt ?? null,
	})
	const scheduledDeactivationDate = formatInactivityDate(signupStatus?.scheduledDeactivationAt, i18n.language)
	const deactivatedDate = formatInactivityDate(signupStatus?.deactivatedAt, i18n.language)
	const isAwaitingVerification = onboardingStatus === 'provisioning_number'
		&& (provisioningStatus === 'awaiting_verification' || provisioningStatus === 'submitting_verification')

	const isProvisioning = (onboardingStatus === 'provisioning_number'
		|| provisioningStatus === 'creating_templates'
		|| provisioningStatus === 'syncing_templates')
		&& provisioningStatus !== 'failed'
		&& !isAwaitingVerification

	// Poll every 5s while provisioning or awaiting verification
	const refetch = signupStatusQuery.refetch
	useEffect(() => {
		if (!isProvisioning && !isAwaitingVerification) { return }

		const interval = setInterval(() => {
			void refetch()
		}, 5000)

		return () => clearInterval(interval)
	}, [isProvisioning, isAwaitingVerification, refetch])

	const {
		templates,
		syncTemplates,
		isSyncing,
		createTemplate,
		isCreating,
		updateParameterMap,
		isUpdatingParameterMap,
		editTemplate,
		isEditing,
		archiveTemplate,
		isArchiving,
		unarchiveTemplate,
		isUnarchiving,
	} = useWhatsAppTemplates(organisationId, { includeArchived: showArchived })

	// First-contact automation must never offer archived templates, regardless of the
	// "show archived" toggle that widens the list for the templates section.
	const activeTemplates = useMemo(() => templates.filter((template) => !template.archivedAt), [templates])

	useEffect(() => {
		if (!organisationId || hasTrackedSettingsViewRef.current || !signupStatusQuery.isFetched) {
			return
		}

		logger.track('WhatsApp: Settings Viewed', {
			category: 'whatsapp',
			data: {
				organisation_id: organisationId,
				has_own_waba: hasOwnWaba,
				is_active: isActive,
				onboarding_status: onboardingStatus,
				provisioning_status: provisioningStatus,
			},
		})
		hasTrackedSettingsViewRef.current = true
	}, [
		hasOwnWaba,
		isActive,
		onboardingStatus,
		organisationId,
		provisioningStatus,
		signupStatusQuery.isFetched,
	])

	const reportFailure = useCallback((
		error: unknown,
		action: string,
		fallbackKey: TranslationKey,
		data: Record<string, unknown> = {},
	) => {
		const display = apiErrorDisplay(error, fallbackKey)
		setError(display.kind === 'translated' ? t(display.key) : display.message)

		const options = {
			category: 'whatsapp',
			data: { action, organisationId, response_message: apiErrorMessage(error), ...data },
		}

		if (isUserActionableError(error)) {
			logger.warn(`WhatsApp: ${action} rejected`, options)
			return
		}

		logger.error(error, options)
	}, [organisationId, t])

	// --- Mutations ---

	const activateAccountMutation = useWhatsAppEmbeddedSignupActivate({
		onSuccess: () => {
			setError('')
			void signupStatusQuery.refetch()
		},
		onError: (err) => {
			reportFailure(err, 'activate-whatsapp-account', 'whatsappSettings.failedToEnable')
		},
	})

	const retryProvisioningMutation = useWhatsAppEmbeddedSignupRetryProvisioning({
		onSuccess: () => {
			setError('')
			void signupStatusQuery.refetch()
		},
		onError: (err) => {
			setSuccess('')
			reportFailure(err, 'retry-provisioning', 'whatsappSettings.failedToRetryProvisioning')
		},
	})

	const disconnectMutation = useWhatsAppEmbeddedSignupDisconnectWhatsApp({
		onSuccess: () => {
			setError('')
			setSuccess(t('whatsappSettings.disconnected'))
			setIsCreatingTemplate(false)
			logger.track('WhatsApp: Disconnected', {
				category: 'whatsapp',
				data: {
					organisation_id: organisationId,
					template_count: templates.length,
				},
			})
			void signupStatusQuery.refetch()
			void queryClient.invalidateQueries({ queryKey: whatsAppTemplateIndexQueryKey(organisationId) })
		},
		onError: (err) => {
			setSuccess('')
			reportFailure(err, 'disconnect-whatsapp', 'whatsappSettings.failedToDisconnect')
		},
	})

	const resetSignupMutation = useWhatsAppEmbeddedSignupResetSignup({
		onSuccess: () => {
			setError('')
			setSuccess(t('whatsappSettings.signupReset'))
			void signupStatusQuery.refetch()
		},
		onError: (err) => {
			reportFailure(err, 'reset-signup', 'whatsappSettings.failedToResetSignup')
		},
	})

	// --- Event handlers ---

	const handleDisconnect = useCallback(async () => {
		try {
			await confirmDialogRef.current?.confirm({
				title: t('whatsappSettings.removeTitle'),
				message: t('whatsappSettings.removeMessage'),
				confirmText: t('whatsappSettings.removeConfirm'),
				cancelText: t('common.cancel'),
			})
			disconnectMutation.mutate({ organisationId })
		} catch {
			// User cancelled
		}
	}, [disconnectMutation])

	const handleSyncTemplates = useCallback(async () => {
		try {
			await syncTemplates()
			setError('')
			setSuccess(t('whatsappSettings.templatesSynced'))
			logger.track('WhatsApp: Templates Synced', {
				category: 'whatsapp',
				data: {
					organisation_id: organisationId,
					template_count_before_sync: templates.length,
				},
			})
		} catch (err) {
			reportFailure(err, 'syncTemplates', 'whatsappSettings.failedToSync')
		}
	}, [organisationId, reportFailure, syncTemplates, t, templates.length])

	const handleCreateTemplate = useCallback(async (data: TemplateFormData) => {
		try {
			await createTemplate(data)
			setError('')
			setSuccess(t('whatsappSettings.templateSubmitted'))
			setIsCreatingTemplate(false)
			logger.track('WhatsApp: Template Created', {
				category: 'whatsapp',
				data: {
					organisation_id: organisationId,
					template_language: data.language,
					template_category: data.category,
					template_parameter_count: data.exampleParameters.length,
				},
			})
		} catch (err) {
			reportFailure(err, 'createTemplate', 'whatsappSettings.failedToCreateTemplate')
		}
	}, [createTemplate, organisationId, reportFailure, t])

	const handleUpdateParameterMap = useCallback(
		async (templateId: string, data: { parameterMap: Record<string, string> | null }) => {
			try {
				await updateParameterMap(templateId, data)
				setError('')
				setSuccess(t('whatsappSettings.autofillSaved'))
				logger.track('WhatsApp: Template Parameter Map Updated', {
					category: 'whatsapp',
					data: {
						organisation_id: organisationId,
						template_id: templateId,
						mapped_count: data.parameterMap ? Object.keys(data.parameterMap).length : 0,
					},
				})
			} catch (err) {
				reportFailure(err, 'updateParameterMap', 'whatsappSettings.autofillSaveErrorTitle', { templateId })
				throw err
			}
		},
		[updateParameterMap, organisationId, reportFailure, t],
	)

	const handleEditTemplate = useCallback(async (templateId: string, data: TemplateFormData) => {
		try {
			await editTemplate(templateId, {
				bodyText: data.bodyText,
				exampleParameters: data.exampleParameters,
			})
			setError('')
			setSuccess(t('whatsappSettings.templateSubmitted'))
			logger.track('WhatsApp: Template Edited', {
				category: 'whatsapp',
				data: { organisation_id: organisationId, template_id: templateId },
			})
		} catch (err) {
			reportFailure(err, 'editTemplate', 'whatsappSettings.failedToEditTemplate', { templateId })
			throw err
		}
	}, [editTemplate, organisationId, reportFailure, t])

	const handleArchiveTemplate = useCallback(async (templateId: string) => {
		try {
			await confirmDialogRef.current?.confirm({
				title: t('whatsappSettings.archiveTemplateTitle'),
				message: t('whatsappSettings.archiveTemplateMessage'),
				confirmText: t('whatsappSettings.archiveTemplateConfirm'),
				cancelText: t('common.cancel'),
			})
		} catch {
			return // user cancelled
		}

		try {
			await archiveTemplate(templateId)
			setError('')
			setSuccess(t('whatsappSettings.templateArchived'))
			logger.track('WhatsApp: Template Archived', {
				category: 'whatsapp',
				data: { organisation_id: organisationId, template_id: templateId },
			})
		} catch (err) {
			reportFailure(err, 'archiveTemplate', 'whatsappSettings.failedToArchiveTemplate', { templateId })
		}
	}, [archiveTemplate, organisationId, reportFailure, t])

	const handleUnarchiveTemplate = useCallback(async (templateId: string) => {
		try {
			await unarchiveTemplate(templateId)
			setError('')
			setSuccess(t('whatsappSettings.templateUnarchived'))
			logger.track('WhatsApp: Template Unarchived', {
				category: 'whatsapp',
				data: { organisation_id: organisationId, template_id: templateId },
			})
		} catch (err) {
			reportFailure(err, 'unarchiveTemplate', 'whatsappSettings.failedToUnarchiveTemplate', { templateId })
		}
	}, [unarchiveTemplate, organisationId, reportFailure, t])

	const handleResetSignup = useCallback(() => {
		resetSignupMutation.mutate({ organisationId })
	}, [resetSignupMutation, organisationId])

	const handleRetryProvisioning = useCallback(() => {
		retryProvisioningMutation.mutate({ organisationId })
	}, [retryProvisioningMutation, organisationId])

	const handleActivateAccount = useCallback(async () => {
		try {
			await confirmDialogRef.current?.confirm({
				title: t('whatsappSettings.activate.confirmAutomation.title'),
				message: t('whatsappSettings.activate.confirmAutomation.message'),
				confirmText: t('whatsappSettings.activate.confirmAutomation.confirm'),
				cancelText: t('common.cancel'),
			})
		} catch {
			return // user cancelled
		}

		setError('')
		setSuccess('')
		logger.track('WhatsApp: Jobilla-Managed Activation Started', {
			category: 'whatsapp',
			data: { organisation_id: organisationId },
		})
		activateAccountMutation.mutate({ organisationId })
	}, [activateAccountMutation, organisationId, t])

	const isLoading = activateAccountMutation.isPending
		|| disconnectMutation.isPending
		|| resetSignupMutation.isPending

	// --- Derived state for rendering conditions ---

	const showProvisioningFailed = onboardingStatus === 'provisioning_number' && provisioningStatus === 'failed'
	const showConnectPanel = !hasOwnWaba && onboardingStatus !== 'failed' && onboardingStatus !== 'provisioning_number'
	const showFailedSignup = onboardingStatus === 'failed'
	const showActiveSection = hasOwnWaba && isActive
	const showBusinessProfile = hasOwnWaba && (isAwaitingVerification || showActiveSection)

	return {
		// State
		error,
		success,
		isCreatingTemplate,
		isLoading,
		isLoadingStatus: signupStatusQuery.isLoading,
		confirmDialogRef,

		// Derived status
		provisioningStatus,
		provisioningError,
		wabaDisplayName,
		isProvisioning,
		organisationId,
		firstContactAutomationEnabled,
		firstContactTemplateId,
		firstContactScoreThreshold,
		refreshStatus: signupStatusQuery.refetch,
		isAwaitingVerification,
		verificationRequestFailed,
		resendCooldownRemaining,
		verificationAutoAttempts,
		verificationOnHold,
		whatsappEnabled,
		showProvisioningFailed,
		showConnectPanel,
		showFailedSignup,
		showActiveSection,
		showBusinessProfile,
		showInactivityWarning,
		wasDeactivatedForInactivity,
		scheduledDeactivationDate,
		deactivatedDate,

		// Templates
		templates,
		activeTemplates,
		isSyncing,
		isCreating,
		isUpdatingParameterMap,
		setIsCreatingTemplate,
		showArchived,
		setShowArchived,
		isEditing,
		isArchiving,
		isUnarchiving,

		// Loading flags
		isRetrying: retryProvisioningMutation.isPending,
		isDisconnecting: disconnectMutation.isPending,
		isActivatingAccount: activateAccountMutation.isPending,
		isResetting: resetSignupMutation.isPending,

		handleRefreshStatus: signupStatusQuery.refetch,

		// Event handlers
		handleResetSignup,
		handleDisconnect,
		handleSyncTemplates,
		handleCreateTemplate,
		handleUpdateParameterMap,
		handleEditTemplate,
		handleArchiveTemplate,
		handleUnarchiveTemplate,
		handleRetryProvisioning,
		handleActivateAccount,
	}
}
